// sales-flow.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BaseApiService } from './base-api-service';
import { BusyService } from './busy-service';
import { FilteredSaleOrder, GetLoadRequest, GetLoadResponse, LoadDetail, ResetLoadToDraftRequest, ResetLoadToDraftResponse } from './load-management.service';

/* ===================== Types ===================== */

export type PostingState = 'draft' | 'posted' | 'cancel';
export type PaymentState =
  | 'not_paid'
  | 'in_payment'
  | 'paid'
  | 'partial'
  | 'reversed';

export interface DeliverInvoiceItem {
  product_id: number;
  quantity: number;
  /** Optional: override unit price on matching SO line before invoicing */
  price_unit?: number;
}

export interface DeliverInvoiceItemsRequest {
  /** Required for idempotency */
  idempotencyKey: string;
  /** SO to deliver & invoice against */
  sale_order_id: number;
  /** Lines to deliver (will cap to remaining deliverable on the SO) */
  items: DeliverInvoiceItem[];

  /** Optional flags (default true server-side) */
  confirm_order?: boolean;
  post_invoice?: boolean;
}

export interface DeliverInvoiceItemsResponse {
  status: 'success' | 'error';
  message?: string;

  sale_order_id?: number;
  sale_order_name?: string;
  sale_order_state?: string;

  /** Pickings validated by the flow */
  picking_ids?: number[];

  /** Created invoice (if any) */
  invoice_id?: number | null;
  invoice_name?: string | null;
  invoice_state?: PostingState | null;

  /** Present when nothing could be delivered */
  note?: string;
}

/** Minimal invoice details fetch (reuse if you already have a richer type elsewhere) */
export interface SimpleInvoiceDetails {
  id: number;
  name: string | null;
  state: PostingState;
  paymentState: PaymentState;
  amountTotal: number;
  amountResidual: number;
  currency: string | null;
}

/**
 * Local augmentation so UI can read the new flag without touching the original service file.
 */
export interface FilteredSaleOrderWithInvoice extends FilteredSaleOrder {
  /** True if there's at least one posted out_invoice for this SO */
  hasPostedInvoice?: boolean;
}

export type GetSalesLoadResponse = Omit<GetLoadResponse, 'load'> & {
  load?: Omit<LoadDetail, 'saleOrders'> & {
    saleOrders: FilteredSaleOrderWithInvoice[];
  };
};

/** ===================== Service ===================== */

@Injectable({ providedIn: 'root' })
export class SalesFlowService extends BaseApiService {
  constructor(http: HttpClient, busy: BusyService) {
    super(http, busy);
  }

  /**
   * POST /v1/api/sales/deliver_invoice_items
   * Delivers EXACT quantities for given products on a Sales Order,
   * then creates (and optionally posts) an invoice that mirrors delivered qty.
   */
  deliverInvoiceItems(
    payload: DeliverInvoiceItemsRequest
  ): Observable<DeliverInvoiceItemsResponse> {
    return this.post<DeliverInvoiceItemsResponse>(
      'sales/deliver_invoice_items',
      payload
    );
  }

  /**
   * Convenience: ensure confirm + post invoice are true unless explicitly disabled.
   */
  deliverAndPost(
    saleOrderId: number,
    idempotencyKey: string,
    items: DeliverInvoiceItem[],
    opts: { confirm?: boolean; post?: boolean } = {}
  ): Observable<DeliverInvoiceItemsResponse> {
    const body: DeliverInvoiceItemsRequest = {
      idempotencyKey,
      sale_order_id: saleOrderId,
      items,
      confirm_order: opts.confirm ?? true,
      post_invoice: opts.post ?? true,
    };
    return this.deliverInvoiceItems(body);
  }

  /** Optional helper to return just the created invoice id (or throw on failure) */
  deliverAndGetInvoiceId(
    saleOrderId: number,
    idempotencyKey: string,
    items: DeliverInvoiceItem[],
    opts: { confirm?: boolean; post?: boolean } = {}
  ): Observable<number> {
    return this.deliverAndPost(saleOrderId, idempotencyKey, items, opts).pipe(
      map((res) => {
        if (res.status === 'success' && res.invoice_id) return res.invoice_id;
        throw new Error(res.message || 'Failed to deliver & invoice');
      })
    );
  }

  /**
   * POST /v1/api/sales/load
   * Fetches a load with its SOs, including `hasPostedInvoice` on each SO.
   * Uses existing GetLoadRequest/Response shapes, with a local augmentation to type the new field.
   */
  getSalesLoad(payload: GetLoadRequest): Observable<GetSalesLoadResponse> {
    return this.post<GetSalesLoadResponse>('sales/load', payload);
  }

  /**
   * POST /v1/api/load/reset_to_draft
   * Resets a LOADED load back to DRAFT (re-open editing).
   */
  resetLoadedLoadToDraft(
    payload: ResetLoadToDraftRequest
  ): Observable<ResetLoadToDraftResponse> {
    return this.post<ResetLoadToDraftResponse>('load/reset_to_draft', payload);
  }
}
